import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, Zap, Users, Shield, Clock, ArrowRight, 
  Building2, Star, Sparkles, Globe, HeadphonesIcon
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 19,
    description: 'Perfect for small teams getting started',
    users: '5 users',
    features: [
      'Lead Management',
      'Deal Pipeline',
      'Contact Management',
      'Basic Reports',
      'Email Support',
      'Activity Timeline',
    ],
    color: 'blue',
    popular: false
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 49,
    description: 'For growing businesses with larger teams',
    users: '20 users',
    features: [
      'Everything in Starter',
      'Advanced Reports',
      'Email Integration',
      'API Access',
      'Priority Support',
      'Data Export (CSV/Excel)',
      'Custom Fields',
    ],
    color: 'purple',
    popular: true
  },
  {
    id: 'business',
    name: 'Business',
    price: 99,
    description: 'For established businesses needing scale',
    users: 'Unlimited users',
    features: [
      'Everything in Growth',
      'Unlimited Users',
      'Dedicated Account Manager',
      'Custom Integrations',
      'Advanced Security',
      'SLA Guarantee',
      'Training Sessions',
      'White-label Option',
    ],
    color: 'orange',
    popular: false
  }
];

const CRMPricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleSelectPlan = (planId) => {
    // Navigate to CRM checkout with selected plan
    navigate(`/crm/checkout?plan=${planId}`);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[150px]"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400">14-Day Free Trial • No Credit Card Required</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Powerful CRM for
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"> Growing Businesses</span>
          </h1>
          
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            Manage leads, track deals, and grow your revenue with our intuitive CRM platform. 
            Start free and scale as you grow.
          </p>

          <div className="flex items-center justify-center gap-8 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>No setup fees</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>24/7 support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-slate-800/50 backdrop-blur border rounded-2xl p-8 ${
                  plan.popular 
                    ? 'border-purple-500/50 scale-105 shadow-xl shadow-purple-500/10' 
                    : 'border-slate-700/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="px-4 py-1 bg-purple-500 rounded-full text-white text-sm font-medium flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-slate-400 text-sm">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400">/month</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Up to {plan.users}</p>
                </div>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  data-testid={`crm-select-${plan.id}`}
                  className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                    plan.popular
                      ? 'bg-purple-500 hover:bg-purple-600 text-white'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>

                <div className="mt-8 space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Everything You Need to Grow
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Team Collaboration', desc: 'Work together with role-based access and activity tracking' },
              { icon: Shield, title: 'Enterprise Security', desc: 'Your data is encrypted and isolated with multi-tenant architecture' },
              { icon: Zap, title: 'Lightning Fast', desc: 'Optimized for speed with instant search and real-time updates' },
              { icon: Globe, title: 'Access Anywhere', desc: 'Web-based platform accessible from any device, anywhere' },
              { icon: HeadphonesIcon, title: '24/7 Support', desc: 'Get help when you need it with our dedicated support team' },
              { icon: Clock, title: 'Quick Setup', desc: 'Get started in minutes with easy onboarding and import tools' },
            ].map((feature, idx) => (
              <div key={idx} className="text-center p-6">
                <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-slate-700/50 rounded-2xl p-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Grow Your Business?
            </h2>
            <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
              Join thousands of companies using CLOUD IT CRM to manage their sales pipeline and close more deals.
            </p>
            <button
              onClick={() => navigate('/crm/checkout?plan=growth')}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-xl inline-flex items-center gap-2 transition-all"
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-sm text-slate-500 mt-4">
              No credit card required • 14-day free trial
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CRMPricing;
